import DataTableDO = App.Data.Explorer.DataTableDO;
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;
import DataTableMetricValueType = App.Enums.DataTableMetricValueType;
import { DataTableMetric } from "@/modules/explorer/components/Metrics/MetricResponseProcessor";
import { ColumnDef, Row } from "@tanstack/vue-table";
import { t } from "@/plugins/i18n";
import { computed, h, WritableComputedRef } from "vue";
import TanstackCell from "@/modules/explorer/components/Metrics/Table/TanstackCell.vue";
import { RowData } from "@tanstack/table-core";
import { formatNumber } from "chartjs-graph";
import dayjs from "dayjs";
import { effectiveState } from "@/Utils/proxy/DerivedModelProxyHandler";
import DocumentDO = App.Data.Explorer.DocumentDO;
import ValueFormat = App.Enums.ValueFormat;
import DocumentDateComparisonDTO = App.Data.Explorer.DocumentDateComparisonDTO;

export const hierarchyRootIsTheTotal = (table: DataTableDO): boolean =>
	table.dimensions.length === 1 && table.dimensions[0]?.type === "hierarchy";

export type TanstackCellContext = {
	metric: DocumentVerticalDTO;
	dataTable: DataTableDO;
	valueType: DataTableMetricValueType;
	comparisonPeriods: string[];
	isBudgetDeltaEnabled: boolean;
};

export const VALUE_FORMATS = [
	"default",
	"money",
	"money_no_fraction",
	"percentage",
	"number_no_fraction",
	"power",
	"energy",
	"time_hour_minute",
	"rate_card",
] as const;

export function mapDimensionIndexToName(
	dimensionIndex: number | string
): string {
	if (dimensionIndex === 0) {
		return "horizontal";
	} else if (dimensionIndex === 1) {
		return "offset";
	}

	return `dimension_${dimensionIndex}`;
}

export function mapDimensionNameToIndex(name: string | null): number | null {
	if (!name) return null;
	if (name === "horizontal") return 0;
	if (name === "offset") return 1;

	const match = name.match(/^dimension_(\d+)$/);
	if (match) {
		return Number(match[1]);
	}
	return null;
}

export function getCellAccessorKey(
	metric: DocumentVerticalDTO | string,
	valueType: DataTableMetricValueType,
	comparison: string
): string {
	const metricNameClean =
		typeof metric === "string" ? metric : metric.name_clean;

	return `${metricNameClean}_${comparison}_${valueType}`;
}

export function extractDimensionValuesFromRow<T>(
	dataTable: DataTableDO,
	row: Row<T>
): Record<string, T> {
	return dataTable.dimensions
		.map((dimension, idx) => mapDimensionIndexToName(idx))
		.reduce((acc, cellKey) => {
			acc[cellKey] = row.original[cellKey];

			return acc;
		}, {});
}

export type PivotTransformResult = {
	pivotedData: Record<string, any>[];
	uniquePivotValues: string[];
};

export function transformDataForPivot(
	data: Record<string, any>[],
	dataTable: DataTableDO
): PivotTransformResult {
	const pivotKey = pivotRowKey(dataTable);

	if (pivotKey === null) {
		return { pivotedData: data, uniquePivotValues: [] };
	}

	const allDimensionKeys = dataTable.dimensions.map((_, idx) =>
		mapDimensionIndexToName(idx)
	);
	const remainingDimensionKeys = allDimensionKeys.filter((k) => k !== pivotKey);

	const uniquePivotValues = [
		...new Set(
			data
				.filter((row) => !row.isTotalRow)
				.map((row) => row[pivotKey])
				.filter((v) => v !== null && v !== undefined)
		),
	] as string[];

	const groups = new Map<string, Record<string, any>>();

	for (const row of data) {
		const groupKey = remainingDimensionKeys.map((k) => row[k]).join("||");

		if (!groups.has(groupKey)) {
			const baseRow: Record<string, any> = { isTotalRow: row.isTotalRow };
			for (const key of remainingDimensionKeys) {
				baseRow[key] = row[key];
			}
			groups.set(groupKey, baseRow);
		}

		const mergedRow = groups.get(groupKey)!;
		const pivotValue = row[pivotKey];

		for (const key of Object.keys(row)) {
			if (!allDimensionKeys.includes(key) && key !== "isTotalRow") {
				mergedRow[`${key}_${pivotValue}`] = row[key];
			}
		}
	}

	return { pivotedData: [...groups.values()], uniquePivotValues };
}

const allowedBudgetComparisonVerticals = [
	"revenuesdriveout",
	"revenuesdrivein",
	"advertising_revenue_distr",
];

export function createMetricObject(
	metric: DocumentVerticalDTO,
	row: Record<string, number>,
	comparisonPeriods: string[] = [],
	comparisonLabel: string
) {
	return new DataTableMetric(
		metric.name_clean,
		metric.format,
		comparisonPeriods
			.filter((comparison) => {
				return !(
					comparison === "Bud" &&
					!allowedBudgetComparisonVerticals.includes(metric.name_clean)
				);
			})
			.map((comparison) => {
				const actualValue =
					row[getCellAccessorKey(metric, "actualValue", comparison)];
				const predictedValue =
					row[getCellAccessorKey(metric, "actualValue", comparison)];
				const totalValue =
					row[getCellAccessorKey(metric, "totalValue", comparison)];
				const currentYearTotalValue =
					row[getCellAccessorKey(metric, "totalValue", comparisonLabel)];
				const deltaAbsolute = currentYearTotalValue - totalValue;
				const deltaRelative = (currentYearTotalValue - totalValue) / totalValue;
				return {
					actualValue,
					predictedValue,
					totalValue,
					deltaAbsolute,
					deltaRelative,
					comparison,
				};
			})
	);
}

export type MetricColumn = ColumnDef<Record<string, string>, any>;

declare module "@tanstack/vue-table" {
	interface ColumnMeta<TData extends RowData, TValue> {
		tdClasses?: string;
		thClasses?: string;
		comparison?: string;
		metricName?: string;
		valueType?: DataTableMetricValueType;
	}
}

function filterHiddenColumns(
	columns: MetricColumn[],
	dataTable: DataTableDO
): any[] {
	if (!Array.isArray(columns)) return [];
	const hidden = dataTable?.hidden_columns || [];

	return columns
		.map((column) => {
			if (
				hidden.includes(column.accessorKey) ||
				hidden.includes(column.meta?.metricName)
			) {
				return null;
			}

			if (Array.isArray(column.columns) && column.columns.length) {
				const filteredChildren = filterHiddenColumns(column.columns, dataTable);
				return filteredChildren.length > 0
					? { ...column, columns: filteredChildren }
					: null;
			}

			return column;
		})
		.filter(Boolean);
}

function suffixAccessorKeys(
	columns: MetricColumn[],
	suffix: string
): MetricColumn[] {
	return columns.map((col) => {
		if ("accessorKey" in col && col.accessorKey) {
			return { ...col, accessorKey: `${col.accessorKey}_${suffix}` };
		}
		return col;
	});
}

function getPivotColumns(
	dataTable: DataTableDO,
	metrics: DocumentVerticalDTO[],
	comparisonPeriods: string[],
	showComparisonDialog: boolean,
	isBudgetDeltaEnabled: boolean,
	uniquePivotValues: string[]
): MetricColumn[] {
	return uniquePivotValues.map((pivotValue) => {
		const metricColumns = dataTable.is_comparison_nested
			? getNestedComparisonColumns(
					dataTable,
					metrics,
					comparisonPeriods,
					showComparisonDialog,
					isBudgetDeltaEnabled
			  )
			: getSplitUpComparisonColumns(
					dataTable,
					metrics,
					comparisonPeriods,
					showComparisonDialog,
					isBudgetDeltaEnabled
			  );

		const suffixed = suffixAccessorKeys(metricColumns, pivotValue);

		if (suffixed.length === 1 && !("columns" in suffixed[0])) {
			return { ...suffixed[0], header: pivotValue };
		}

		return {
			header: pivotValue,
			columns: suffixed,
		};
	});
}

export function generateColumns(
	dataTable: DataTableDO,
	metrics: DocumentVerticalDTO[],
	comparisonPeriods: string[],
	isBudgetDeltaEnabled: boolean,
	filterColumns: boolean = true,
	excludeDimensions: boolean = false,
	uniquePivotValues: Array<string> = [],
	columnsIncludedAfterCustomSql: Array<string> = []
) {
	const showComparisonDialog =
		dataTable.is_comparison_nested && comparisonPeriods.length > 1;

	const pivotKey = pivotRowKey(dataTable);
	const isPivoting = pivotKey !== null && uniquePivotValues.length > 0;

	let columns: MetricColumn[] = [];

	if (dataTable.custom_sql && columnsIncludedAfterCustomSql.length !== 0) {
		return getCustomSqlColumns(columnsIncludedAfterCustomSql);
	}
	if (!excludeDimensions) {
		dataTable.dimensions.forEach((dimension, idx) => {
			if (isPivoting && mapDimensionIndexToName(idx) === pivotKey) {
				return;
			}

			const accessorKey = mapDimensionIndexToName(idx);
			const column = {
				header: t(`properties.${dimension.name_clean}.title`),
				accessorKey,
			};

			const customFormats = new Map([
				["day", "DD MMM YYYY"],
				["month", "MMM YYYY"],
			]);
			const format = customFormats.get(dimension.granularity);
			if (format) {
				column["cell"] = ({ getValue }) => {
					if (!getValue()) {
						return "";
					}
					if (getValue() === "Total") return "Total";
					return dayjs(getValue()).format(format);
				};
			}
			columns.push(column);
		});
	}

	if (isPivoting) {
		const pivotDimension = dataTable.dimensions.find(
			(dimension) => dimension.name_clean === dataTable.pivot_dimension
		);

		columns = [
			...columns,
			{
				id: "pivot",
				header: t(`properties.${pivotDimension.name_clean}.title`),
				meta: { thClasses: "text-center" },
				columns: getPivotColumns(
					dataTable,
					metrics,
					comparisonPeriods,
					showComparisonDialog,
					isBudgetDeltaEnabled,
					uniquePivotValues
				),
			},
		];
	} else if (dataTable.is_comparison_nested) {
		columns = [
			...columns,
			...getNestedComparisonColumns(
				dataTable,
				metrics,
				comparisonPeriods,
				showComparisonDialog,
				isBudgetDeltaEnabled
			),
		];
	} else {
		columns = [
			...columns,
			...getSplitUpComparisonColumns(
				dataTable,
				metrics,
				comparisonPeriods,
				showComparisonDialog,
				isBudgetDeltaEnabled
			),
		];
	}

	// We only want to filter the columns for the UI in the data table, not for helper functions.
	if (filterColumns) {
		columns = filterHiddenColumns(columns, dataTable);
	}
	if (columnsIncludedAfterCustomSql.length !== 0) {
		columns = columns
			.map((column) => {
				if (columnsIncludedAfterCustomSql.includes(column.accessorKey)) {
					return column;
				}
				return null;
			})
			.filter(Boolean);
	}

	return columns;
}

function getNestedComparisonColumns(
	dataTable: DataTableDO,
	metrics: DocumentVerticalDTO[],
	comparisonPeriods: string[],
	showComparisonDialog: boolean,
	isBudgetDeltaEnabled: boolean
): MetricColumn[] {
	return metrics.map((metric) => {
		const header = t(`properties.${metric.name_clean}.title`);
		if (dataTable.split_prediction) {
			return {
				header,
				meta: { thClasses: `text-center` },
				columns: [
					getCell(
						metric.id,
						"Act",
						metric,
						dataTable,
						comparisonPeriods,
						"actualValue",
						[
							{
								thClasses: `text-center`,
								tdClasses: `text-right`,
								metricName: metric.name_clean,
								comparison: "Y",
								valueType: "actualValue",
							},
						],
						"Y",
						0,
						false,
						isBudgetDeltaEnabled
					),
					getCell(
						metric.id,
						"Pred",
						metric,
						dataTable,
						comparisonPeriods,
						"totalValue",
						[
							{
								thClasses: `text-center`,
								tdClasses: `text-right`,
								metricName: metric.name_clean,
								comparison: "Y",
								valueType: "totalValue",
							},
						],
						"Y",
						0,
						showComparisonDialog,
						isBudgetDeltaEnabled
					),
				],
			};
		}

		return getCell(
			metric.id,
			header,
			metric,
			dataTable,
			comparisonPeriods,
			"totalValue",
			[
				{
					thClasses: `text-center`,
					tdClasses: `text-right`,
					metricName: metric.name_clean,
					comparison: comparisonPeriods[0],
					valueType: "totalValue",
				},
			],
			comparisonPeriods[0],
			0,
			showComparisonDialog,
			isBudgetDeltaEnabled
		);
	});
}

function getSplitUpComparisonColumns(
	dataTable: DataTableDO,
	metrics: DocumentVerticalDTO[],
	comparisonPeriods: string[],
	showComparisonDialog: boolean,
	isBudgetDeltaEnabled: boolean
): MetricColumn[] {
	return metrics.map((metric) => {
		let filteredComparisonPeriods = comparisonPeriods;
		if (
			comparisonPeriods.includes("Bud") &&
			!allowedBudgetComparisonVerticals.includes(metric.name_clean)
		) {
			filteredComparisonPeriods = comparisonPeriods.filter((p) => p !== "Bud");
		}
		const header = t(`properties.${metric.name_clean}.title`);
		const metas = filteredComparisonPeriods.map((comparisonPeriod) => {
			return {
				thClasses: `text-center`,
				tdClasses: `text-right`,
				metricName: metric.name_clean,
				comparison: comparisonPeriod,
				valueType: "totalValue",
			};
		});

		if (
			dataTable.split_prediction ||
			(filteredComparisonPeriods.length > 1 &&
				(dataTable.show_change_absolute ||
					dataTable.show_change_relative_to_current)) ||
			(isBudgetDeltaEnabled && metric.name_clean !== "revenuesdriveout")
		) {
			// No real comparison selected (only the base period): with split prediction
			// on, render the Act/Pred columns directly under the metric and skip the
			// redundant per-comparison subheader (e.g. "Y").
			if (
				dataTable.split_prediction &&
				filteredComparisonPeriods.length === 1
			) {
				const valueTypes: {
					valueType: DataTableMetricValueType;
					label: string;
				}[] = [
					{ valueType: "actualValue", label: "Act" },
					{ valueType: "totalValue", label: "Pred" },
				];

				return {
					header,
					meta: { thClasses: `text-center` },
					columns: valueTypes.map((valueType) =>
						getCell(
							metric.id,
							valueType.label,
							metric,
							dataTable,
							filteredComparisonPeriods,
							valueType.valueType,
							metas,
							filteredComparisonPeriods[0],
							0,
							showComparisonDialog,
							isBudgetDeltaEnabled
						)
					),
				};
			}

			return {
				header,
				meta: { thClasses: `text-center` },
				columns: filteredComparisonPeriods.map((comparisonPeriod, idx) => {
					let valueTypes: {
						valueType: DataTableMetricValueType;
						label: string;
					}[] = [
						{
							valueType: "totalValue",
							label: "Pred",
						},
					];

					if (
						dataTable.split_prediction &&
						comparisonPeriod === filteredComparisonPeriods[0]
					) {
						valueTypes = [
							{ valueType: "actualValue", label: "Act" },
							{ valueType: "totalValue", label: "Pred" },
						];
					} else if (comparisonPeriod !== filteredComparisonPeriods[0]) {
						valueTypes = [{ valueType: "totalValue", label: "Pred" }];
						if (dataTable.show_change_absolute) {
							valueTypes.push({ valueType: "deltaAbsolute", label: "ΔAbs" });
						}
						if (dataTable.show_change_relative_to_current) {
							valueTypes.push({ valueType: "deltaRelative", label: "ΔRel" });
						}
					}

					return {
						header: comparisonPeriod,
						columns: valueTypes.map((valueType) => {
							return getCell(
								metric.id,
								valueType.label,
								metric,
								dataTable,
								filteredComparisonPeriods,
								valueType.valueType,
								metas,
								comparisonPeriod,
								idx,
								showComparisonDialog,
								isBudgetDeltaEnabled
							);
						}),
						meta: metas[idx],
					};
				}),
			};
		}

		if (filteredComparisonPeriods.length === 1) {
			return getCell(
				metric.id,
				header,
				metric,
				dataTable,
				filteredComparisonPeriods,
				"totalValue",
				metas,
				filteredComparisonPeriods[0],
				0,
				showComparisonDialog,
				isBudgetDeltaEnabled
			);
		}

		return {
			header,
			meta: { thClasses: `text-center` },
			columns: filteredComparisonPeriods.map((comparisonPeriod, idx) => {
				return getCell(
					metric.id,
					comparisonPeriod,
					metric,
					dataTable,
					filteredComparisonPeriods,
					"totalValue",
					metas,
					comparisonPeriod,
					idx,
					showComparisonDialog,
					isBudgetDeltaEnabled
				);
			}),
		};
	});
}

function getCustomSqlColumns(
	columnsIncludedAfterCustomSql: string[]
): MetricColumn[] {
	return columnsIncludedAfterCustomSql.map((columnName) => {
		let baseName = columnName;
		let valueType: ValueFormat = "default";

		for (const format of VALUE_FORMATS) {
			const suffix = `_${format}`;

			if (columnName.toLowerCase().endsWith(suffix.toLowerCase())) {
				valueType = format as ValueFormat;
				baseName = columnName.slice(0, -suffix.length);
				break;
			}
		}

		return {
			accessorKey: columnName,
			header: baseName,
			meta: {
				thClasses: valueType === "default" ? "" : `text-right`,
				tdClasses: valueType === "default" ? "" : `text-right`,
			},
			cell: ({ getValue }) => {
				const value = getValue();

				if (value === null || value === undefined) {
					return "-";
				}

				if (typeof value === "string") {
					return value;
				}

				return formatNumber(value, valueType);
			},
		};
	});
}
function getCell(
	metricId: number,
	header: string,
	metric: DocumentVerticalDTO,
	dataTable: DataTableDO,
	comparisonPeriods: string[],
	valueType: DataTableMetricValueType,
	metas: any[],
	comparisonPeriod: string,
	idx: number,
	showComparisonDialog: boolean,
	isBudgetDeltaEnabled: boolean
): MetricColumn {
	return {
		metricId,
		header,
		accessorKey: getCellAccessorKey(metric, valueType, comparisonPeriod),
		cell(props) {
			if (!showComparisonDialog) {
				return formatNumber(
					props.cell.getValue(),
					valueType === "deltaRelative" ? "percentage" : metric.format
				);
			}

			return h(TanstackCell, {
				cell: props.cell,
				value: props.cell.getValue(),
				context: {
					metric,
					dataTable,
					comparisonPeriods,
					valueType,
					isBudgetDeltaEnabled,
				},
			});
		},
		meta: { ...metas[idx], comparison: comparisonPeriod, valueType },
	};
}

export function getCurrentDataTable(
	document: DocumentDO,
	derivedDocument: DocumentDO | null
): WritableComputedRef<DataTableDO> {
	const docDataTables = effectiveState(
		() => document,
		() => derivedDocument,
		"data_tables"
	);

	return computed({
		get: () => docDataTables.value[0],
		set: (value) => {
			docDataTables.value = [value];
		},
	});
}

export function updateCurrentDataTable(
	document: DocumentDO,
	derivedDocument: DocumentDO | null,
	dataTable: DataTableDO
) {
	const docDataTables = effectiveState(
		() => document,
		() => derivedDocument,
		"data_tables"
	);

	const tables = docDataTables.value;

	tables[0] = { ...dataTable };

	docDataTables.value = tables;
}

export const hasActiveComparison = (dc: DocumentDateComparisonDTO): boolean =>
	(dc.periods?.length ?? 0) > 0 ||
	(dc.customPeriods?.length ?? 0) > 0 ||
	!!dc.isBudgetComparisonActive;

export const pivotBlockReason = (
	dataTable: DataTableDO,
	dateComparison: DocumentDateComparisonDTO
): string | null => {
	if (hasActiveComparison(dateComparison))
		return "Pivoting isn't available while a comparison is active.";
	if (dataTable.metrics.length !== 1)
		return "Pivoting needs exactly one metric.";
	if (dataTable.split_prediction)
		return "Pivoting isn't available with split prediction.";
	return null;
};

export const pivotRowKey = (dataTable: DataTableDO): string | null => {
	const idx = dataTable.dimensions.findIndex(
		(d) => d.name_clean === dataTable.pivot_dimension
	);

	return idx === -1 ? null : mapDimensionIndexToName(idx);
};
